import { useMemo } from 'react';

import { Box } from '#/components/Box/Box';
import { Icon } from '#/components/Icon/Icon';
import { createTab } from '#/components/Tabs/createTab';
import { TabsHorizontal } from '#/components/Tabs/Horizontal/TabsHorizontal';
import { useQueryParamState } from '#/hooks/useQueryParamState';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiOrg } from '#/modules/listing/orgs/api/types/ApiOrg';
import { colors, spacing } from '#/theme/theme';
import type { DatetimeString } from '#/types/TypedDateString';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { ViewOrgPageTruncatedListings } from './components/ViewOrgPageTruncatedListings';
import type { ViewOrgPageListingTab } from './types/ViewOrgPageListingTab';
import { isViewOrgPageListingTab } from './types/ViewOrgPageListingTab';
import { ViewOrgPageListingsTabsTabLabel } from './ViewOrgPageListingsTabsLabel';

type Props = {
  org: ApiOrg;
  defaultTab: ViewOrgPageListingTab;
  now: DatetimeString;
};

export function ViewOrgPageListingsTabs({ org, defaultTab, now }: Props) {
  const { i18n } = useI18n();

  const [queryTabSlug, setTabSlug] = useQueryParamState({
    name: 'tab',
  });

  const tabSlug = isViewOrgPageListingTab(queryTabSlug)
    ? queryTabSlug
    : defaultTab;

  const tabs = useMemo(
    () =>
      [
        org.jobs.length > 0 &&
          createTab({
            prefix: 'org-listings',
            value: 'JOB',
            label: (
              <ViewOrgPageListingsTabsTabLabel
                $active={tabSlug === 'JOB'}
                count={org.jobCount}
                icon={<Icon name="briefcase" colorName="white" size={24} />}
              >
                {i18n('Jobs')}
              </ViewOrgPageListingsTabsTabLabel>
            ),
          }),
        org.internships.length > 0 &&
          createTab({
            prefix: 'org-listings',
            value: 'INTERNSHIP',
            label: (
              <ViewOrgPageListingsTabsTabLabel
                $active={tabSlug === 'INTERNSHIP'}
                count={org.internshipCount}
                icon={<Icon name="sign-path" colorName="white" size={24} />}
              >
                {i18n('Internships')}
              </ViewOrgPageListingsTabsTabLabel>
            ),
          }),
        org.volops.length > 0 &&
          createTab({
            prefix: 'org-listings',
            value: 'VOLOP',
            label: (
              <ViewOrgPageListingsTabsTabLabel
                $active={tabSlug === 'VOLOP'}
                count={org.volopCount}
                icon={<Icon name="hand" colorName="white" size={24} />}
              >
                {i18n('Volunteering')}
              </ViewOrgPageListingsTabsTabLabel>
            ),
          }),
        org.events.length > 0 &&
          createTab({
            prefix: 'org-listings',
            value: 'EVENT',
            label: (
              <ViewOrgPageListingsTabsTabLabel
                $active={tabSlug === 'EVENT'}
                count={org.eventCount}
                icon={<Icon name="star-calendar" colorName="white" size={24} />}
              >
                {i18n('Events')}
              </ViewOrgPageListingsTabsTabLabel>
            ),
          }),
      ].filter(Boolean),
    [i18n, org, tabSlug],
  );

  const [currentListings, currentListingCount] = useMemo(() => {
    switch (tabSlug) {
      case 'JOB':
        return [org.jobs, org.jobCount];
      case 'INTERNSHIP':
        return [org.internships, org.internshipCount];
      case 'VOLOP':
        return [org.volops, org.volopCount];
      case 'EVENT':
        return [org.events, org.eventCount];
    }
  }, [org, tabSlug]);

  return (
    <>
      <Box borderBottom={`1px solid ${colors.calloutGrey}`}>
        <TabsHorizontal
          tabs={tabs}
          value={tabSlug}
          onSelectTab={(value) => {
            setTabSlug(value);
            trackClicked('Listing Type Tab on Org Page', {
              listing_type: value,
            });
          }}
          compact
        />
      </Box>

      <Box mt={spacing[40]}>
        <ViewOrgPageTruncatedListings
          listingCount={currentListingCount}
          listingType={tabSlug}
          listings={currentListings}
          orgId={org.id}
          now={now}
        />
      </Box>
    </>
  );
}
